import { useAuth0 } from "@auth0/auth0-react";
import { useContext, useState } from "react";
import { RefreshContext } from "../../ContextProvider";
import { baseURL } from "../../helpers/fetchingURL";
import Button from "../Reusables/Button";
import Modal from "../Reusables/Modal";
import Spinner from "../Utility/Spinner";

// product_id, product_quantity and product_price hold " | " separated values for multi-item orders
function splitField(value) {
  return String(value).split(" | ");
}

function getShopOrderId(item) {
  return item.shop === "EBAY" ? item.ebay_order_id : item.ShopifyId;
}

function toMintsoftOrder(item) {
  const productIds = splitField(item.product_id);
  const quantities = splitField(item.product_quantity);
  const prices = splitField(item.product_price);

  return {
    // Table id, echoed back by the server so each result can be matched to its order
    id: item.id,
    OrderItems: productIds.map((id, index) => ({
      SKU: id,
      Quantity: quantities[index],
      UnitPrice: prices[index],
    })),
    orderId: getShopOrderId(item),
    // Mintsoft specific data
    ExternalOrderReference: item.order_number,
    FirstName: item.shipping_first_name,
    LastName: item.shipping_last_name,
    Address1: item.shipping_address1,
    Address2: item.shipping_address2,
    Address3: "",
    Town: item.shipping_city,
    PostCode: item.shipping_zip,
    Country: item.shipping_country_code,
    Phone: item.shipping_phone,
    Mobile: item.shipping_phone,
    Email: item.contact_email,
    ShippingMethod: item.ShippingMethod,
    created_at: item.created_at,
    shop: item.shop,
  };
}

async function sendJson(url, method, accessToken, body) {
  const response = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`${method} ${url} failed with status ${response.status}`);
  }

  return response.json();
}

const ApprovalModal = ({ data, isOpen, setIsOpen, toStatus }) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const { setRefreshData } = useContext(RefreshContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Set after a submit with failures: { succeeded, failures: [{ label, error }] }
  const [outcome, setOutcome] = useState(null);

  let verb = "";
  let color = "";
  switch (toStatus) {
    case "Approved":
      verb = "approve";
      color = "green";
      break;
    case "Rejected":
      verb = "reject";
      color = "red";
      break;
    case "Dispatched":
      verb = "dispatch";
      color = "blue";
      break;
    default:
      break;
  }

  function close() {
    setOutcome(null);
    setIsOpen(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setIsSubmitting(true);

    const failures = [];
    let succeeded = 0;
    const orderNumberById = new Map(
      data.map((item) => [item.id, item.order_number]),
    );
    const collectFailures = (results) => {
      results
        .filter((result) => !result.success)
        .forEach((result) =>
          failures.push({
            label: orderNumberById.get(result.id) ?? result.id,
            error: result.error,
          }),
        );
    };

    try {
      const accessToken = await getAccessTokenSilently();

      // Dispatching also sets the status server side, so there is no second call to make:
      // the status is saved as each order goes through, even if this request times out
      if (toStatus === "Dispatched") {
        const { results } = await sendJson(
          `${baseURL}/api/order/`,
          "POST",
          accessToken,
          { name: user.nickname, orders: data.map(toMintsoftOrder) },
        );
        console.log("mintsoft results", results);

        collectFailures(results);
        succeeded = results.filter((result) => result.success).length;
      } else {
        const { results } = await sendJson(
          `${baseURL}/api/order/status`,
          "PATCH",
          accessToken,
          {
            name: user.nickname,
            status: toStatus,
            tableOrderIds: data.map((item) => item.id),
            orderId: data.map(getShopOrderId),
            shop: data.map((item) => item.shop),
          },
        );
        console.log("status results", results);

        collectFailures(results);
        succeeded = results.filter((result) => result.success).length;
      }
    } catch (error) {
      // A whole request failed (network, auth, server crash), so we can't tell which orders went through
      console.log(error);
      failures.push({
        label: "Request failed",
        error: `${error.message}. Some orders may already have gone through, so check the table before retrying.`,
      });
    }

    setRefreshData((prevState) => !prevState);
    setIsSubmitting(false);

    if (failures.length === 0) {
      close();
    } else {
      setOutcome({ succeeded, failures });
    }
  }

  if (outcome) {
    return (
      <Modal isOpen={isOpen} setIsOpen={close}>
        <div className="w-full rounded bg-white p-4 shadow">
          <h1 className="text-lg">Some orders could not be {toStatus.toLowerCase()}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {outcome.succeeded}{" "}
            {outcome.succeeded === 1 ? "order was" : "orders were"} updated to{" "}
            {toStatus}. The rest were left unchanged.
          </p>
          <ul className="mb-6 mt-3 max-h-64 space-y-2 overflow-y-auto">
            {outcome.failures.map((failure, index) => (
              <li
                key={`${failure.label}-${index}`}
                className="rounded border border-red-200 bg-red-50 p-2 text-sm"
              >
                <p className="font-medium">{failure.label}</p>
                <p className="break-words text-red-700">{failure.error}</p>
              </li>
            ))}
          </ul>
          <Button type="button" onClick={close}>
            Close
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} setIsOpen={!isSubmitting ? setIsOpen : () => {}}>
      <form
        className="w-full p-4 bg-white rounded shadow"
        onSubmit={handleSubmit}
      >
        <h1 className="text-lg">
          Are you sure you want to {verb}{" "}
          <span className="font-medium underline underline-offset-2">
            {data.length}
          </span>{" "}
          {data.length > 1 ? "orders" : "order"}?
        </h1>
        <div className="mt-3 mb-6">
          <p className="text-sm text-gray-500">
            {data.length > 1 ? "Orders" : "Order"}:
          </p>
          <div className="grid grid-cols-6">
            {data.map((item) => (
              <p className="text-sm" key={item.order_number}>
                {item.order_number}
              </p>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 space-x-2">
          <Button color={color} className="capitalize" disabled={isSubmitting}>
            <Spinner show={isSubmitting} className={"h-4"} />
            <span>{verb}</span>
          </Button>
          <Button
            type="button"
            disabled={isSubmitting}
            onClick={() => setIsOpen(false)}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ApprovalModal;
